import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { useCheckMinibarVisible } from './CheckBatchMinibar';
import { HaiIcon } from './icons';

/**
 * BackToTopButton — 右下角「回到頂部」浮動鈕(掛 AppShell,全站共用)。
 * -----------------------------------------------------------------------------
 * **捲動容器是 window**:AppShell 是 `min-height:100vh` 的 flex row,長頁把容器
 * 撐高後捲的是整份文件(main 的 `overflow-y:auto` 因為被 stretch 到內容高而不會
 * 內捲)。`mainFillHeight` 的頁(canvas)容器恰好 100vh、window 永遠不捲 →
 * `scrollY` 恆 0 → 這顆鈕天然不出現,不需要 per-page 開關。
 *
 * 出現門檻 {@link SHOW_AFTER_PX};低於門檻不 render(不是 `opacity:0` —— 隱形
 * 但仍在的元素會擋住底下的東西)。
 *
 * 位置與 `CheckBatchMinibar` 同一角落 → minibar 在時自己往上讓一格,判準共用
 * {@link useCheckMinibarVisible}(兩處各寫一次遲早漂移)。
 */

/** 捲過這個距離才顯示(約半個視窗高;太小會在短頁一直閃出來)。 */
const SHOW_AFTER_PX = 320;

/** minibar 高度 + 間距(讓開它,不疊在上面)。 */
const MINIBAR_CLEARANCE = 56;

export function BackToTopButton() {
  const { t } = useTranslation('common');
  const minibarVisible = useCheckMinibarVisible();
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const sync = () => setShown(window.scrollY > SHOW_AFTER_PX);
    sync(); // 冷啟/換頁時已經在半途(瀏覽器捲動還原)也要對
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, []);

  if (!shown) return null;

  const toTop = () => {
    // 使用者關掉動態效果時不做 smooth(prefers-reduced-motion)。
    // `?.matches` 不是多餘的 —— 測試環境的 matchMedia 是個 stub,未知 query 回 undefined。
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)')?.matches === true;
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <button
      type="button"
      onClick={toTop}
      title={t('actions.backToTop')}
      aria-label={t('actions.backToTop')}
      data-testid="back-to-top"
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--tide-soft)';
        e.currentTarget.style.color = 'var(--tide-primary-hover)';
        e.currentTarget.style.borderColor = 'var(--tide-200)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--surface-pop)';
        e.currentTarget.style.color = 'var(--ink-500)';
        e.currentTarget.style.borderColor = 'var(--border-pop)';
      }}
      style={{
        ...btnStyle,
        bottom: minibarVisible ? 20 + MINIBAR_CLEARANCE : 20,
      }}
    >
      <HaiIcon name="sortUp" size={16} />
    </button>
  );
}

const btnStyle: CSSProperties = {
  position: 'fixed',
  right: 20,
  /* minibar 是 100;比它低一階 —— 兩者同時在時 minibar 是主角 */
  zIndex: 95,
  width: 40,
  height: 40,
  borderRadius: 12,
  display: 'grid',
  placeItems: 'center',
  /* R218 浮層層級:深色比卡片再亮一階(與 minibar 同一組 token) */
  background: 'var(--surface-pop)',
  border: '1px solid var(--border-pop)',
  color: 'var(--ink-500)',
  boxShadow: 'var(--shadow-lg)',
  cursor: 'pointer',
  padding: 0,
  transition: 'background .12s, color .12s, border-color .12s, bottom .15s',
};
